import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { studentAPI } from '../../utils/api';
import { FaUser, FaCreditCard, FaClipboardCheck, FaBriefcase, FaCheckCircle, FaClock, FaTimes } from 'react-icons/fa';
import { toast } from 'react-toastify';

const Dashboard = () => {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await studentAPI.getDashboard();
      setDashboard(response.data.dashboard);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const getPaymentStatusBadge = (status) => {
    const badges = {
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: FaClock, text: 'Pending' },
      submitted: { color: 'bg-blue-100 text-blue-800', icon: FaClock, text: 'Submitted' },
      verified: { color: 'bg-green-100 text-green-800', icon: FaCheckCircle, text: 'Verified' },
      rejected: { color: 'bg-red-100 text-red-800', icon: FaTimes, text: 'Rejected' }
    };
    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${badge.color}`}>
        <Icon size={14} />
        {badge.text}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-secondary-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-secondary-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-secondary-900">Student Dashboard</h1>
          <p className="text-secondary-600 mt-2">Welcome back, {dashboard?.profile.name}!</p>
        </div>

        {/* Status Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white p-6 rounded-xl shadow-md">
            <div className="flex items-center justify-between mb-4">
              <div className="bg-blue-100 p-3 rounded-lg">
                <FaUser className="text-blue-600" size={24} />
              </div>
            </div>
            <h3 className="text-secondary-600 text-sm font-medium">Profile Status</h3>
            <p className="text-2xl font-bold text-secondary-900 mt-2">Active</p>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-md">
            <div className="flex items-center justify-between mb-4">
              <div className="bg-green-100 p-3 rounded-lg">
                <FaCreditCard className="text-green-600" size={24} />
              </div>
            </div>
            <h3 className="text-secondary-600 text-sm font-medium">Payment Status</h3>
            <div className="mt-2">{getPaymentStatusBadge(dashboard?.payment.status)}</div>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-md">
            <div className="flex items-center justify-between mb-4">
              <div className="bg-purple-100 p-3 rounded-lg">
                <FaClipboardCheck className="text-purple-600" size={24} />
              </div>
            </div>
            <h3 className="text-secondary-600 text-sm font-medium">Applications</h3>
            <p className="text-2xl font-bold text-secondary-900 mt-2">
              {dashboard?.application.appliedCount}
            </p>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-md">
            <div className="flex items-center justify-between mb-4">
              <div className="bg-orange-100 p-3 rounded-lg">
                <FaBriefcase className="text-orange-600" size={24} />
              </div>
            </div>
            <h3 className="text-secondary-600 text-sm font-medium">Available</h3>
            <p className="text-2xl font-bold text-secondary-900 mt-2">
              {dashboard?.availableInternships}
            </p>
          </div>
        </div>

        {/* Payment Alert */}
        {dashboard?.payment.status === 'pending' && (
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 rounded-lg mb-8">
            <div className="flex items-center">
              <FaClock className="text-yellow-600 mr-3" size={24} />
              <div>
                <h3 className="text-lg font-semibold text-yellow-800">Payment Pending</h3>
                <p className="text-yellow-700 mt-1">
                  Please complete your payment to access internship applications.
                </p>
                <Link
                  to="/student/payment"
                  className="inline-block mt-3 bg-yellow-600 text-white px-6 py-2 rounded-lg hover:bg-yellow-700 transition-colors"
                >
                  Complete Payment
                </Link>
              </div>
            </div>
          </div>
        )}

        {dashboard?.payment.status === 'submitted' && (
          <div className="bg-blue-50 border-l-4 border-blue-400 p-6 rounded-lg mb-8">
            <div className="flex items-center">
              <FaClock className="text-blue-600 mr-3" size={24} />
              <div>
                <h3 className="text-lg font-semibold text-blue-800">Payment Under Verification</h3>
                <p className="text-blue-700 mt-1">
                  Your payment is being verified by our admin team. This usually takes 24-48 hours.
                </p>
                <p className="text-sm text-blue-600 mt-2">
                  UTR Number: <span className="font-mono font-bold">{dashboard?.payment.utrNumber}</span>
                </p>
              </div>
            </div>
          </div>
        )}

        {dashboard?.payment.status === 'rejected' && (
          <div className="bg-red-50 border-l-4 border-red-400 p-6 rounded-lg mb-8">
            <div className="flex items-center">
              <FaTimes className="text-red-600 mr-3" size={24} />
              <div>
                <h3 className="text-lg font-semibold text-red-800">Payment Rejected</h3>
                <p className="text-red-700 mt-1">
                  Your payment verification failed. Please submit again with correct UTR number.
                </p>
                <Link
                  to="/student/payment"
                  className="inline-block mt-3 bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700 transition-colors"
                >
                  Resubmit Payment
                </Link>
              </div>
            </div>
          </div>
        )}

        {/* Profile Information */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          <div className="bg-white p-6 rounded-xl shadow-md">
            <h2 className="text-xl font-bold text-secondary-900 mb-4">Profile Information</h2>
            <div className="space-y-3">
              <div>
                <span className="text-secondary-600 text-sm">Name:</span>
                <p className="font-medium">{dashboard?.profile.name}</p>
              </div>
              <div>
                <span className="text-secondary-600 text-sm">Email:</span>
                <p className="font-medium">{dashboard?.profile.email}</p>
              </div>
              <div>
                <span className="text-secondary-600 text-sm">College:</span>
                <p className="font-medium">{dashboard?.profile.collegeName}</p>
              </div>
              <div>
                <span className="text-secondary-600 text-sm">Course:</span>
                <p className="font-medium">{dashboard?.profile.courseName}</p>
              </div>
            </div>
            <Link
              to="/student/profile"
              className="inline-block mt-4 text-primary-600 hover:text-primary-700 font-medium"
            >
              Edit Profile →
            </Link>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-md">
            <h2 className="text-xl font-bold text-secondary-900 mb-4">Payment Details</h2>
            <div className="space-y-3">
              <div>
                <span className="text-secondary-600 text-sm">Amount Paid:</span>
                <p className="font-bold text-2xl text-primary-600">₹{dashboard?.payment.amount}</p>
              </div>
              <div>
                <span className="text-secondary-600 text-sm">Status:</span>
                <div className="mt-1">{getPaymentStatusBadge(dashboard?.payment.status)}</div>
              </div>
              {dashboard?.payment.utrNumber && (
                <div>
                  <span className="text-secondary-600 text-sm">UTR Number:</span>
                  <p className="font-mono font-medium">{dashboard?.payment.utrNumber}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Recent Internships */}
        {dashboard?.payment.status === 'verified' && (
          <div className="bg-white p-6 rounded-xl shadow-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-secondary-900">Available Internships</h2>
              <Link
                to="/internships"
                className="text-primary-600 hover:text-primary-700 font-medium"
              >
                View All →
              </Link>
            </div>
            {dashboard?.recentInternships && dashboard.recentInternships.length > 0 ? (
              <div className="space-y-4">
                {dashboard.recentInternships.map((internship) => (
                  <div
                    key={internship._id}
                    className="border border-secondary-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                  >
                    <h3 className="font-semibold text-lg text-secondary-900">{internship.title}</h3>
                    <p className="text-secondary-600 text-sm mt-1">{internship.company}</p>
                    <div className="flex flex-wrap gap-4 mt-2 text-sm text-secondary-600">
                      <span>{internship.location}</span>
                      <span>•</span>
                      <span>{internship.duration}</span>
                      <span>•</span>
                      <span>{internship.stipend}</span>
                    </div>
                    <Link
                      to={`/internships/${internship._id}`}
                      className="inline-block mt-3 text-primary-600 hover:text-primary-700 font-medium"
                    >
                      View Details →
                    </Link>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-secondary-600 text-center py-8">No internships available at the moment</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
